import React from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box, Paper
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CircleIcon from '@mui/icons-material/Circle';

type Props = {
    historyItem: any;
    isOpen: boolean;
    onClose: () => void;
};

const StatusViewer: React.FC<Props> = ({ historyItem, isOpen, onClose }) => {
    const parsedAdditional = React.useMemo(() => {
        if (!historyItem?.additionalData) return null;
        try {
            return JSON.parse(historyItem.additionalData) as Record<string, unknown>;
        } catch {
            return null;
        }
    }, [historyItem?.additionalData]);

    if (!isOpen || !historyItem) return null;

    const formatKey = (key: string) =>
        key.replace(/([A-Z])/g, ' $1').trim().toLowerCase()
            .replace(/^\w/, c => c.toUpperCase());

    const formatValue = (key: string, value: unknown): string => {
        if (typeof value === 'number') {
            return key === 'value'
                ? value.toLocaleString()
                : String(value);
        }
        return String(value);
    };

    const initials = (name: string) =>
        name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

    const statusDate = new Date(historyItem.statusDate);

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            scroll="paper"
            PaperProps={{
                sx: {
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                },
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    px: 3,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoOutlinedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
                        Status details
                    </Typography>
                </Box>

                {/* Status badge */}
                <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.6,
                    fontSize: 12,
                    fontWeight: 500,
                    px: 1.25,
                    py: 0.4,
                    borderRadius: '99px',
                    bgcolor: 'info.50',
                    color: 'info.main',
                    border: '0.5px solid',
                    borderColor: 'info.light',
                }}>
                    <CircleIcon sx={{ fontSize: 8 }} />
                    {historyItem.statusName}
                </Box>
            </DialogTitle>

            <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />

            <DialogContent
                dividers={false}
                sx={{
                    flex: 1,
                    px: 3,
                    py: 2.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                {/* Status + Date */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <FieldBlock label="Status" value={historyItem.statusName} />
                    <FieldBlock
                        label="Date & time"
                        value={`${statusDate.toLocaleDateString()} at ${statusDate.toLocaleTimeString()}`}
                    />
                </Box>

                {/* Notes */}
                {historyItem.notes && (
                    <SectionCard title="Notes">
                        <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: 'text.primary' }}>
                            {historyItem.notes}
                        </Typography>
                    </SectionCard>
                )}

                {/* Additional Information */}
                {parsedAdditional && (
                    <SectionCard title="Additional information">
                        {Object.entries(parsedAdditional).map(([key, value]) => (
                            <Box
                                key={key}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'baseline',
                                    py: 0.6,
                                    borderBottom: '0.5px solid',
                                    borderColor: 'divider',
                                    gap: 2,
                                    '&:last-child': { borderBottom: 'none' },
                                }}
                            >
                                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                                    {formatKey(key)}
                                </Typography>
                                <Typography sx={{ fontSize: 13, fontWeight: 500, textAlign: 'right' }}>
                                    {formatValue(key, value)}
                                </Typography>
                            </Box>
                        ))}
                    </SectionCard>
                )}

                {/* Changed by */}
                {historyItem.changedBy && (
                    <Box>
                        <Typography sx={{
                            fontSize: 11, fontWeight: 500, color: 'text.secondary',
                            textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5
                        }}>
                            Changed by
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Box sx={{
                                width: 28, height: 28, borderRadius: '50%',
                                bgcolor: 'info.50', color: 'info.main',
                                fontSize: 11, fontWeight: 500,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                            }}>
                                {initials(historyItem.changedBy)}
                            </Box>
                            <Typography sx={{ fontSize: 14 }}>{historyItem.changedBy}</Typography>
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    py: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                }}
            >
                <Button variant="outlined" size="small" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ── Small reusable helpers ──────────────────────────────────────────

const FieldBlock = ({ label, value }: { label: string; value: string }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{
            fontSize: 11, fontWeight: 500, color: 'text.secondary',
            textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5
        }}>
            {label}
        </Typography>
        <Typography sx={{ fontSize: 14, color: 'text.primary' }}>{value}</Typography>
    </Box>
);

const SectionCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <Paper
        elevation={0}
        sx={{
            bgcolor: 'background.secondary',
            border: '0.5px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            p: 1.5,
        }}
    >
        <Typography sx={{
            fontSize: 11, fontWeight: 500, color: 'text.secondary',
            textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1
        }}>
            {title}
        </Typography>
        {children}
    </Paper>
);

export default StatusViewer;